import { computed, ReadonlySignal, Signal, signal } from '@preact/signals'
import { BrowserHelper } from '@scandit/web-datacapture-core'

import type AppStore from './AppStore'
import Sentry from '../sentry.config'

export type TErrorsStore = {
  hasPartialSupport: Signal<boolean>
  hasUnsupportedBrowserMessage: ReadonlySignal<boolean>
  hasCameraAccessError: ReadonlySignal<boolean>
  unsupportedBrowserMessage: Signal<string | undefined>
  handleError: (error: Error) => void
  licenseError: Signal<string | undefined>
  hasLicenseError: ReadonlySignal<boolean>
  hasNoCameraAvailableError: ReadonlySignal<boolean>
}

type CameraAccessErrorType =
  | 'NotAllowedError'
  | 'AbortError'
  | 'NoCameraAvailableError'
  | 'NotFoundError'
  | 'NotReadableError'
  | 'SecurityError'

const ErrorsStore = (_appStore: AppStore): TErrorsStore => {
  const unsupportedBrowserMessage = signal<string | undefined>(undefined)

  const cameraAccessError = signal<CameraAccessErrorType | undefined>(undefined)
  const cameraAvailableError = signal<CameraAccessErrorType | undefined>(undefined)

  const hasCameraAccessError = computed(() => cameraAccessError.value !== undefined)
  const hasNoCameraAvailableError = computed(() => cameraAvailableError.value !== undefined)
  const hasUnsupportedBrowserMessage = computed(() => unsupportedBrowserMessage.value !== undefined)

  const compatibility = BrowserHelper.checkBrowserCompatibility()
  const hasPartialSupport = signal(!compatibility.fullSupport && compatibility.scannerSupport)

  const licenseError = signal<string | undefined>(undefined)
  const hasLicenseError = computed(() => licenseError.value !== undefined)

  const handleUnsupportedBrowserError = (error: any) => {
    if (error.name === 'UnsupportedBrowserError') {
      unsupportedBrowserMessage.value = error.name
      if (error.data?.missingFeatures.length > 0) {
        unsupportedBrowserMessage.value = `${error.data.missingFeatures.join(', ')}.`
      }
    }
  }

  const handleCameraError = (error: any) => {
    switch (error.name) {
      case 'NoCameraAvailableError':
      case 'NotFoundError':
        cameraAvailableError.value = error.name
        break
      case 'NotAllowedError':
      case 'AbortError':
      case 'NotReadableError':
      case 'SecurityError':
        cameraAccessError.value = error.name as CameraAccessErrorType
        break
    }
  }

  const handleLicenseError = (error: Error) => {
    if (error.name === 'NoLicenseKeyError') {
      licenseError.value = error.name
    }
  }

  const handleError = (error: Error) => {
    handleLicenseError(error)
    handleUnsupportedBrowserError(error)
    handleCameraError(error)

    /*alert("Unfortunately something went wrong. We've been notified of the issue")*/
    console.error(error.message ? error.message : error)
    Sentry.captureException(error)
  }

  return {
    handleError,
    unsupportedBrowserMessage,
    hasCameraAccessError,
    hasNoCameraAvailableError,
    hasUnsupportedBrowserMessage,
    hasPartialSupport,
    licenseError,
    hasLicenseError,
  }
}

export default ErrorsStore
